import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, message, Space, Spin, Tag, Typography } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { useCompanyData } from '../../../../../../../../Hooks/TT_ELD/Companies';
import {
  useCompanyEldKey,
  useMigrationStatus,
  useRetryMigration,
} from '../../../../../../../../Hooks/TT_ELD/LegalMigration';
import { apiErrorMessage, MigrationDirection } from '../../../../../../../../API/TT_ELD/legalMigration';
import MigrationProgress, { DIRECTION_LABEL } from './MigrationProgress';
import MigrationModal, { pricePending } from './MigrationModal';
import SubscriptionSummary from './SubscriptionSummary';
import { DEFAULT_ELD, STANDARD_ELD } from '../../../../../../../../Utils/eldTypeLabels';

const { Text } = Typography;

// Who can start migrations / set the price from this tab (the tab itself uses the same roles)
export const ELD_MIGRATION_ROLES = ['manager', 'sales'];

interface Props {
  role: string;
}

const EldMigration = ({ role }: Props) => {
  const { id } = useParams<{ id: string }>();
  const companyId = id!;
  const canManage = ELD_MIGRATION_ROLES.includes(role);

  const { data: company, isLoading: companyLoading } = useCompanyData(companyId);
  const { data: companyKey } = useCompanyEldKey(companyId);
  const { data: status, isLoading: statusLoading } = useMigrationStatus(companyId);
  const retry = useRetryMigration(companyId);

  const [modal, setModal] = useState<{ direction: MigrationDirection; step: 'migrate' | 'price' } | null>(null);
  const [pending, setPending] = useState(() => pricePending.get(companyId));

  if (companyLoading || statusLoading) return <Spin style={{ display: 'block', margin: 40 }} />;

  const isLegal = company?.config?.eldType === 'custom';
  const running = status?.status === 'running';
  // The pending price belongs to the migration that put the company on its current ELD type
  const migrationMatchesType = !!status && (status.direction === 'to_legal') === isLegal;

  const onRetry = async () => {
    if (!status) return;
    try {
      await retry.mutateAsync(status.direction);
      message.success('Failed drivers were queued again');
    } catch (error) {
      message.error(apiErrorMessage(error));
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
          <Descriptions.Item label="Company type">
            <Tag color={isLegal ? 'blue' : 'default'}>{isLegal ? STANDARD_ELD : DEFAULT_ELD}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="ELD key">
            {isLegal
              ? companyKey?.eldKeyName
                ? `${companyKey.eldKeyName}${companyKey.usesDefault ? ' (default)' : ''}`
                : 'Deployment key'
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Last migration">
            {status ? `${DIRECTION_LABEL[status.direction]} · ${status.status}` : 'Never migrated'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Migration">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {running ? (
            <Alert
              type="info"
              showIcon
              message="A migration is in progress. A new one can start once it has finished and every pending driver has switched."
            />
          ) : !canManage ? null : isLegal ? (
            <Space wrap>
              <Text>This company is on {STANDARD_ELD}. Move it back to {DEFAULT_ELD}:</Text>
              <Button
                danger
                icon={<SwapOutlined />}
                onClick={() => setModal({ direction: 'to_non_legal', step: 'migrate' })}
              >
                Switch to {DEFAULT_ELD}
              </Button>
            </Space>
          ) : (
            <Space wrap>
              <Text>This company is on {DEFAULT_ELD}. Move it to {STANDARD_ELD}:</Text>
              <Button
                type="primary"
                icon={<SwapOutlined />}
                onClick={() => setModal({ direction: 'to_legal', step: 'migrate' })}
              >
                Switch to {STANDARD_ELD}
              </Button>
            </Space>
          )}

          {status ? (
            <MigrationProgress status={status} canManage={canManage} retrying={retry.isLoading} onRetry={onRetry} />
          ) : (
            <Text type="secondary">This company has never been migrated.</Text>
          )}
        </Space>
      </Card>

      <Card title="Subscription">
        <SubscriptionSummary
          companyId={companyId}
          company={company}
          canManage={canManage}
          pricePending={pending && migrationMatchesType}
          onSetPrice={() => setModal({ direction: isLegal ? 'to_legal' : 'to_non_legal', step: 'price' })}
        />
      </Card>

      {modal && (
        <MigrationModal
          companyId={companyId}
          company={company}
          direction={modal.direction}
          initialStep={modal.step}
          onPricePendingChange={setPending}
          onClose={() => setModal(null)}
        />
      )}
    </Space>
  );
};

export default EldMigration;
